// Theme: follows the system unless the user picks one. The choice lives on
// <html data-tema> (read by tokens.css) and in localStorage (index.html
// applies it before first paint to avoid a light flash at night).
import { useSyncExternalStore } from 'react';

export type PreferenciaTema = 'sistema' | 'claro' | 'oscuro';

const CLAVE = 'tm-tema';
const oyentes = new Set<() => void>();

function leer(): PreferenciaTema {
  try {
    const v = localStorage.getItem(CLAVE);
    return v === 'claro' || v === 'oscuro' ? v : 'sistema';
  } catch {
    return 'sistema';
  }
}

let preferencia: PreferenciaTema = typeof window === 'undefined' ? 'sistema' : leer();

export function fijarTema(p: PreferenciaTema) {
  preferencia = p;
  try {
    if (p === 'sistema') localStorage.removeItem(CLAVE);
    else localStorage.setItem(CLAVE, p);
  } catch {
    // Private mode: the choice lasts for this session only.
  }
  const html = document.documentElement;
  // Disable transitions for one frame so the whole UI switches at once
  // instead of every surface animating its colors independently.
  html.classList.add('[&_*]:transition-none!');
  if (p === 'sistema') delete html.dataset['tema'];
  else html.dataset['tema'] = p;
  requestAnimationFrame(() =>
    requestAnimationFrame(() => html.classList.remove('[&_*]:transition-none!')),
  );
  for (const o of oyentes) o();
}

const consultaOscuro = () => window.matchMedia('(prefers-color-scheme: dark)');

function suscribir(cb: () => void) {
  oyentes.add(cb);
  const mq = consultaOscuro();
  mq.addEventListener('change', cb);
  return () => {
    oyentes.delete(cb);
    mq.removeEventListener('change', cb);
  };
}

export function useTema() {
  const pref = useSyncExternalStore(
    suscribir,
    () => preferencia,
    () => 'sistema' as const,
  );
  const oscuroSistema = useSyncExternalStore(
    suscribir,
    () => consultaOscuro().matches,
    () => false,
  );
  const efectivo: 'claro' | 'oscuro' =
    pref === 'sistema' ? (oscuroSistema ? 'oscuro' : 'claro') : pref;
  return { preferencia: pref, efectivo, fijar: fijarTema };
}
